import React, { lazy, Suspense, useEffect, useState } from "react";
import "./main.scss";
import Sidebar from "../sidebar/Sidebar";
import Navbars from "../navbars/Navbars";
import { Route, Routes, useNavigate, Navigate } from "react-router-dom";
import { useGetMeQuery } from "../../services/auth";
import { IS_HR_SITE, HR_DEFAULT_ROUTE, isHrRole } from "../../config/access";

// Route-level code splitting: each page below is its own chunk, fetched
// only when its route is actually visited. The HR build never routes to
// the main-only pages, so those chunks are never requested by HR users —
// that's what keeps the HR portal's runtime footprint scoped to what it's
// allowed to see, even though both builds share this one file.
const Dashboard = lazy(() => import("../dashboard/Dashboard"));
const Users = lazy(() => import("../user-management/user-list/Users"));
const ActiveUsers = lazy(() => import("../user-management/active-users/ActiveUsers"));
const RecentUsers = lazy(() => import("../user-management/recent-users/RecentUsers"));
const Listeners = lazy(() => import("../listener-management/listeners-list/Listeners"));
const ApplicationRequests = lazy(() =>
  import("../listener-management/application-requests/ApplicationRequests")
);
const ProfileApproval = lazy(() =>
  import("../listener-management/profile-approvals/ProfileApproval")
);
const PaymentList = lazy(() => import("../payment-management/payment-list/PaymentList"));
const SalaryPyout = lazy(() => import("../payment-management/salary-payout/SalaryPyout"));
const Reconcile = lazy(() => import("../payment-management/reconcile/Reconcile"));
const Recharge = lazy(() => import("../recharge-charges/recharge-plans/Recharge"));
const GiftManagement = lazy(() => import("../recharge-charges/gift-management/GiftManagement"));
const CoupenManagement = lazy(() =>
  import("../recharge-charges/coupen-management/CoupenManagement")
);
const ChargeManagement = lazy(() =>
  import("../recharge-charges/charge-management/ChargeManagement")
);
const ReportBlock = lazy(() => import("../contact-quires-manage/report-block/ReportBlock"));
const PenaltyManage = lazy(() => import("../recharge-charges/penalty-manage/Penaltymanage"));
const ListenerProfileView = lazy(() =>
  import("../listener-management/listener-profile-view/ListenerProfileView")
);
const Docs = lazy(() => import("../listener-management/docs/Docs"));
const UserProfile = lazy(() => import("../user-management/user-profile-view/UserProfile"));
const ListenerDetailsForm = lazy(() =>
  import("../listener-management/listener-detail-form/ListenerDetailsForm")
);
const PendingImages = lazy(() => import("../listener-management/pending-images/PendingImages"));
const PendingProfileUpdates = lazy(() =>
  import("../listener-management/pending-profile-updates/PendingProfileUpdates")
);
const EditSalary = lazy(() => import("../payment-management/payment-list/edit-salary/EditSalary"));
const SalarySlip = lazy(() => import("../payment-management/payment-list/salary-slip/SalarySlip"));
const Status = lazy(() => import("../status/Status"));
const SendNotification = lazy(() => import("../notifications/SendNotification"));
const SessionAnalyticsOverview = lazy(() =>
  import("../session-analytics/overview/SessionAnalyticsOverview")
);
const UserAnalytics = lazy(() => import("../session-analytics/users/UserAnalytics"));
const UserAnalyticsDetail = lazy(() => import("../session-analytics/users/UserAnalyticsDetail"));
const ListenerAnalytics = lazy(() => import("../session-analytics/listeners/ListenerAnalytics"));
const ListenerAnalyticsDetail = lazy(() =>
  import("../session-analytics/listeners/ListenerAnalyticsDetail")
);
const InactiveUsers = lazy(() => import("../session-analytics/inactive/InactiveUsers"));
const InactiveListeners = lazy(() => import("../session-analytics/inactive/InactiveListeners"));
const PeakHours = lazy(() => import("../session-analytics/peak-hours/PeakHours"));

const Main = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(
    () => localStorage.getItem("sidebarCollapsed") === "true"
  );
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };
  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };
  useEffect(() => {
    localStorage.setItem("sidebarCollapsed", String(isCollapsed));
  }, [isCollapsed]);
  const {
    data: user,
    refetch,
    isLoading: isUserLoading,
    error: userError,
  } = useGetMeQuery(null, {
    skip: !localStorage.getItem("token"),
  });
  const navigate = useNavigate();
  useEffect(() => {
    if (!user && !isUserLoading && userError) {
      navigate("/");
    }
  }, [user, navigate]);

  // Defense in depth: a build only ever mints a session for its matching
  // role at login (see Login.jsx), but if a stale/foreign session somehow
  // ends up here, don't render the wrong portal for it.
  useEffect(() => {
    if (!user) return;
    const role = user?.user?.role;
    const mismatched = IS_HR_SITE ? !isHrRole(role) : isHrRole(role);
    if (mismatched) {
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("role");
      navigate("/");
    }
  }, [user, navigate]);

  return (
    <>
      <div
        className="main-section"
        style={{ "--sidebar-w": isCollapsed ? "84px" : "260px" }}
      >
        <Sidebar
          isSidebarOpen={isSidebarOpen}
          closeSidebar={closeSidebar}
          isCollapsed={isCollapsed}
          setIsCollapsed={setIsCollapsed}
        />
        <div id="main" className="main">
          <Navbars toggleSidebar={toggleSidebar} />
          <Suspense fallback={null}>
            <Routes>
              <Route path="/user-management/users-list" element={<Users />} />
              <Route path="/user-management/active-users" element={<ActiveUsers />} />
              <Route path="/user-management/recent-users" element={<RecentUsers />} />
              <Route path="user-management/profile-view" element={<UserProfile />} />

              <Route path="/listener-management/listeners-list" element={<Listeners />} />
              <Route
                path="/listener-management/listener-application-request"
                element={<ApplicationRequests />}
              />
              <Route
                path="/listener-management/listeners-profile-approvals"
                element={<ProfileApproval />}
              />
              <Route
                path="/listener-management/listeners-profile-approvals-docs"
                element={<Docs />}
              />
              <Route path="/listener-management/pending-images" element={<PendingImages />} />
              <Route
                path="/listener-management/pending-profile-updates"
                element={<PendingProfileUpdates />}
              />
              <Route
                path="/listener-management/profile-view"
                element={<ListenerProfileView />}
              />
              <Route
                path="listener-management/profile-form"
                element={<ListenerDetailsForm />}
              />

              <Route path="/session-analytics/users" element={<UserAnalytics />} />
              <Route path="/session-analytics/user-detail" element={<UserAnalyticsDetail />} />
              <Route path="/session-analytics/listeners" element={<ListenerAnalytics />} />
              <Route
                path="/session-analytics/listener-detail"
                element={<ListenerAnalyticsDetail />}
              />
              <Route path="/session-analytics/inactive-users" element={<InactiveUsers />} />
              <Route
                path="/session-analytics/inactive-listeners"
                element={<InactiveListeners />}
              />

              {!IS_HR_SITE && (
                <>
                  <Route path="/analytics" element={<Dashboard />} />

                  <Route
                    path="/session-analytics"
                    element={<Navigate to="/dashboard/session-analytics/overview" replace />}
                  />
                  <Route
                    path="/session-analytics/overview"
                    element={<SessionAnalyticsOverview />}
                  />
                  <Route path="/session-analytics/peak-hours" element={<PeakHours />} />

                  <Route path="/payment-management/payment-list" element={<PaymentList />} />
                  <Route
                    path="/payment-management/recharge"
                    element={<PaymentList fixedTab="Recharge" />}
                  />
                  <Route
                    path="/payment-management/gift"
                    element={<PaymentList fixedTab="Gift" />}
                  />
                  <Route
                    path="/payment-management/sessions"
                    element={<PaymentList fixedTab="Services" />}
                  />
                  <Route
                    path="/payment-management/payout"
                    element={<PaymentList fixedTab="Payout" />}
                  />
                  <Route path="/payment-management/edit-salary" element={<EditSalary />} />
                  <Route path="/payment-management/salary-slip" element={<SalarySlip />} />
                  <Route path="/payment-management/salary-payout" element={<SalaryPyout />} />
                  <Route path="/payment-management/reconcile" element={<Reconcile />} />

                  <Route path="/recharge-charges/plans" element={<Recharge />} />
                  <Route
                    path="/recharge-charges/gift-manage"
                    element={<GiftManagement />}
                  />
                  <Route
                    path="/recharge-charges/coupen-manage"
                    element={<CoupenManagement />}
                  />
                  <Route
                    path="/recharge-charges/charge-manage"
                    element={<ChargeManagement />}
                  />
                  <Route
                    path="/recharge-charges/penalty-manage"
                    element={<PenaltyManage />}
                  />
                  <Route path="/status" element={<Status />} />
                  <Route path="/notifications/send" element={<SendNotification />} />
                  <Route
                    path="/contact-queries/report-block"
                    element={<ReportBlock />}
                  />
                </>
              )}

              {IS_HR_SITE && (
                <Route path="*" element={<Navigate to={HR_DEFAULT_ROUTE} replace />} />
              )}
            </Routes>
          </Suspense>
        </div>
      </div>
    </>
  );
};

export default Main;
